import { UserCog } from 'lucide-react';
import {
  CampaignAccessDenied,
  CampaignPageShell,
  CampaignTabs,
} from '@/client/components/admin/Campaigns/CampaignShell';
import CampaignSendForm from '@/client/components/admin/Campaigns/CampaignSendForm';
import CampaignSendHistory from '@/client/components/admin/Campaigns/CampaignSendHistory';
import CampaignTemplatesPanel from '@/client/components/admin/Campaigns/CampaignTemplatesPanel';
import CampaignNotificationTypesPanel from '@/client/components/admin/Campaigns/CampaignNotificationTypesPanel';
import CampaignUserCommsPanel from '@/client/components/admin/Campaigns/CampaignUserCommsPanel';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useTabSearchParam } from '@/client/hooks/useTabSearchParam';

const MANAGE_TABS = ['compose', 'history', 'templates', 'types', 'users'] as const;
type Tab = (typeof MANAGE_TABS)[number];

const ManagePage = () => {
  useBreadcrumbs([
    { label: 'Campaigns', href: '/admin/relationship/manage?tab=compose' },
    { label: 'Manage' },
  ]);

  const viewPerm = useHasPermission('messaging.view');
  const sendPerm = useHasPermission('messaging.send');
  const templatePerm = useHasPermission('messaging.template');
  const managePerm = useHasPermission('messaging.manage');
  const overridePerm = useHasPermission('messaging.override');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canSend = sendPerm.isSuperAdmin || sendPerm.hasPermission;
  const canTemplate = templatePerm.isSuperAdmin || templatePerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;
  const canOverride = overridePerm.isSuperAdmin || overridePerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const [tab, setTab] = useTabSearchParam<Tab>(MANAGE_TABS, 'compose');
  const tabs = [
    ...(canSend ? [{ id: 'compose' as const, label: 'Compose' }] : []),
    ...(canView ? [{ id: 'history' as const, label: 'All sends' }] : []),
    ...(canView || canTemplate
      ? [{ id: 'templates' as const, label: 'Templates' }]
      : []),
    ...(canView || canManage
      ? [{ id: 'types' as const, label: 'Notification types' }]
      : []),
    ...(canView || canManage ? [{ id: 'users' as const, label: 'User lookup' }] : []),
  ];
  const activeTab = tabs.some((t) => t.id === tab) ? tab : tabs[0]?.id ?? 'history';

  if (permLoading) {
    return (
      <CampaignPageShell
        loading
        icon={<UserCog className="h-4 w-4 text-white" />}
        title="Manage"
        description=""
      >
        {null}
      </CampaignPageShell>
    );
  }

  if (!canView && !canSend && !canTemplate && !canManage) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <CampaignAccessDenied code="messaging.view" />
        </div>
      </div>
    );
  }

  return (
    <CampaignPageShell
      icon={<UserCog className="h-4 w-4 text-white" />}
      title="Manage"
      description="Cross-channel compose, email/SMS templates, notification-type gates, and per-user comms."
    >
      <CampaignTabs tabs={tabs} active={activeTab} onChange={setTab} />

      {activeTab === 'compose' && canSend && (
        <CampaignSendForm
          defaultChannels={['email', 'sms', 'in_app']}
          canOverride={canOverride}
        />
      )}
      {activeTab === 'history' && canView && <CampaignSendHistory />}
      {activeTab === 'templates' && (canView || canTemplate) && (
        <CampaignTemplatesPanel canWrite={canTemplate} />
      )}
      {activeTab === 'types' && (canView || canManage) && (
        <CampaignNotificationTypesPanel canWrite={canManage} />
      )}
      {activeTab === 'users' && (canView || canManage) && (
        <CampaignUserCommsPanel canManage={canManage} />
      )}
    </CampaignPageShell>
  );
};

export default ManagePage;
